const ONE = BigInt("1000000000000000000");
const HALF = BigInt("500000000000000000");
const TOO_EARLY = BigInt(
  "-57896044618658097711785492504343953926634992332820282019728792003956564819968"
);

export function decodeOutcome(price: string, outcomes?: string[]): string {
  const bn = BigInt(price || "0");
  if (bn === ONE) return "Yes";
  if (bn === BigInt(0)) return "No";
  if (bn === HALF) return "unknown/50-50";
  if (bn === TOO_EARLY) return "Early request";

  // Multi-outcome market: price is an index (0.0, 0.1, 0.2, etc.)
  const decimal = Number(bn) / 1e18;

  // If we have outcomes array, try to map the price to an outcome name
  if (outcomes && outcomes.length > 0) {
    // Price is scaled: 0.0 = outcome[0], 0.1 = outcome[1], etc. (1/n increments)
    const step = 1 / outcomes.length;
    const index = Math.round(decimal / step);
    if (index >= 0 && index < outcomes.length) {
      return outcomes[index];
    }
  }

  // For standard binary markets, any value not 0, 0.5, or 1 is likely invalid
  // Show it as a percentage with "Invalid?" indicator
  const pct = decimal * 100;
  return `Invalid (${pct.toFixed(0)}%)`;
}

export function getOutcomeColor(price: string): string {
  const bn = BigInt(price || "0");
  if (bn === ONE) return "bg-green-500";
  if (bn === BigInt(0)) return "bg-red-500";
  if (bn === HALF) return "bg-blue-500";
  if (bn === TOO_EARLY) return "bg-purple-500";
  return "bg-gray-500";
}

export function getOutcomeTextColor(price: string): string {
  const bn = BigInt(price || "0");
  if (bn === ONE) return "text-green-600 dark:text-green-400";
  if (bn === BigInt(0)) return "text-red-600 dark:text-red-400";
  if (bn === HALF) return "text-blue-600 dark:text-blue-400";
  if (bn === TOO_EARLY) return "text-purple-600 dark:text-purple-400";
  return "text-gray-600 dark:text-gray-400";
}

export function getOutcomeBadgeClass(price: string): string {
  const bn = BigInt(price || "0");
  if (bn === ONE)
    return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
  if (bn === BigInt(0))
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
  if (bn === HALF)
    return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
  if (bn === TOO_EARLY)
    return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400";
  return "bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400";
}

/** Format BigInt wei string (18 decimals) to human-readable UMA amount */
export function formatTokenAmount(raw: string): string {
  try {
    const bn = BigInt(raw || "0");
    const num = Number(bn) / 1e18;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    if (num >= 1) return num.toFixed(0);
    return num.toFixed(2);
  } catch {
    return "0";
  }
}

/** Format subgraph BigDecimal string (already in UMA tokens, not wei) */
export function formatDecimalAmount(raw: string): string {
  try {
    const num = parseFloat(raw || "0");
    if (isNaN(num)) return "0";
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    if (num >= 1) return num.toFixed(0);
    return num.toFixed(2);
  } catch {
    return "0";
  }
}

export function formatBond(bond: string): string | null {
  try {
    const raw = BigInt(bond || "0");
    if (raw === BigInt(0)) return null;
    const num = Number(raw) / 1e6;
    if (num < 0.01) return null;
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
    return `$${num.toFixed(2)}`;
  } catch {
    return null;
  }
}

export function truncateAddress(addr: string): string {
  if (!addr || addr.length < 10) return addr || "\u2014";
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

export function timeAgo(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const STATE_COLORS: Record<string, string> = {
  Disputed:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  Proposed:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  Resolved:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  Settled:
    "bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400",
};

export function formatVolume(volume: number): string {
  if (volume >= 1_000_000) return `$${(volume / 1_000_000).toFixed(1)}M`;
  if (volume >= 1_000) return `$${(volume / 1_000).toFixed(0)}K`;
  return `$${volume.toFixed(0)}`;
}
